import { observer } from 'mobx-react';
import React from 'react';
import { Link, PromiseMessage, PromiseRetry, routerStore } from 'lib/components';
import { dateFormat, msToHumanTime, ReactNodeGenerator } from 'lib/helpers';
import { pagedListResponseUtils, PagedListResponse } from '../../../domain';
import { routes } from 'pages/Routes';
import { EssayStudentListSceneStore } from './EssayStudentListSceneStore';
import { EssayStudentListResponseItem } from 'scenes/essay/EssayModels';
import { EssayStatus } from 'domain/enums';
import Swal from 'sweetalert2';
import { EssaySelect } from 'lib/components/select/EssayStatusSelect';
import { withTranslation, WithTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

interface IProps extends WithTranslation {

}

@observer
class EssayStudentListScene extends React.Component<IProps> {
  store: EssayStudentListSceneStore;
  t: TFunction;

  constructor(props: IProps) {
    super(props);
    this.store = new EssayStudentListSceneStore();
    this.t = props.t;
  }

  componentDidMount() {
    this.store.load();
  }

  render() {
    return Array.from(this.renderer(this.store, this.t));
  }

  *renderRows(store: EssayStudentListSceneStore, t: any): ReactNodeGenerator {
    if (store.loader?.isResolved !== true)
      return;

    const essayItems = store.loader.value.Items;

    for (let item of essayItems) {

      yield (
        <tr key={item.EssayId}>
          <td>
            {essayItems.indexOf(item) + 1}
          </td>
          <td>
            {item.TeacherName}
          </td>
          <td>
            {item.Title}
          </td>
          <td>
            {dateFormat(item.CreatedAt, 'wymdhm', true)}
          </td>
          <td>
            {item.StatusStr}
          </td>
          <td>
            <button type='button' className="btn btn-primary" onClick={(e) => this.onEssayOpenBtnClick(e, item)}>
              {t('button.open')}
            </button>
          </td>
        </tr>
      );
    }
  }

  *renderer(store: EssayStudentListSceneStore, t: any): ReactNodeGenerator {
    if (store.loader == null)
      return;
    const state = store.loader;

    yield (<PromiseMessage key="loader-state"
      label={t('essay.loading')} state={state} align="right" />);

    yield (<PromiseRetry key="retry-actions"
      state={state} retry={store.load} align="right" />);

    if (!state.isResolved)
      return;

    const essayItems = state.value.Items;
    const essayRespons: PagedListResponse<EssayStudentListResponseItem> =
    {
      PageSize: 8,
      CurrentPage: store.currentPage,
      CountRecords: state.value.AllItemsCount,
      Data: essayItems,
    };


    const U = pagedListResponseUtils;
    yield (
      <div className="row" key="response-count">

        <div className="col-sm-12 col-md-6">
          <div className="row" key="add-form">
            <div className="col-auto ml-6">
              <button type="button" className={store.filterDate == "Day" ? "btn btn-primary" : "btn btn-outline-primary"}
                onClick={store.getDay} value="test">{t('filter.day')}
              </button>

              <button type="button" className={store.filterDate == "Week" ? "btn btn-primary" : "btn btn-outline-primary"}
                onClick={store.getWeek} value="test" >{t('filter.week')}
              </button>

              <button type="button" className={store.filterDate == "Month" ? "btn btn-primary" : "btn btn-outline-primary"}
                onClick={store.getWeek} value="test" >{t('filter.month')}
              </button>

              <button type="button" className={store.filterDate == "Year" ? "btn btn-primary" : "btn btn-outline-primary"}
                onClick={store.getWeek} value="test" >{t('filter.year')}
              </button>
            </div>
            <div className="md-col-sm-12 col-md-5">
              <EssaySelect value={store.status.value}
                onChange={store.status.setValue} />
            </div>
          </div>
        </div>
        <div className="col-auto ml-auto">
          {t('filter.total', { total: essayRespons.CountRecords, currentPage: essayRespons.CurrentPage + 1, pages: Math.ceil(essayRespons.CountRecords / 8) })}
          <button type="button" className="btn btn-primary"
            disabled={!U.hasPreviousPage(essayRespons)}
            onClick={store.loadPreviousPage}>
            <span className="fas fa-angle-left" /> {t('filter.prev')}
          </button>
          <button type="button" className="btn btn-primary"
            onClick={store.load}>
            <span className="fas fa-sync" /> {t('filter.refresh')}
          </button>
          <button type="button" className="btn btn-primary"
            onClick={store.resetResults}>
            <span className="fas fa-sync" /> {t('filter.reset')}
          </button>
          <button type="button" className="btn btn-primary"
            disabled={!U.hasNextPage(essayRespons)}
            onClick={store.loadNextPage}>
            {t('filter.next')}
            <span className="fas fa-angle-right pl-1" />
          </button>
        </div>
      </div>);
    if (essayItems.length === 0) {
      yield (
        <div className="row justify-content-center" key="no-classrooms">
          <div className="col-auto">
            <div className="alert alert-info">{t('essay.no-essay')}</div>
          </div>
        </div>
      );
      return;
    }

    yield (
      <div className="row" key="schedule">
        <div className="col">
          <table className="table table-sm table-bordered table-hover">
            <thead>
              <tr>
                <th>{t('essay.table.number')} </th>
                <th>{t('essay.table.teacher')} </th>
                <th>{t('essay.table.title')} </th>
                <th>{t('essay.table.create-date')} </th>
                <th>{t('essay.table.status')} </th>
                <th>{t('essay.table.action')} </th>
              </tr>
            </thead>
            <tbody>
              {Array.from(this.renderRows(store, t))}
            </tbody>
          </table>
        </div>
      </div>);
  }

  componentWillUnmount() {
    this.store.dispose();
  }

  onEssayOpenBtnClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, essay: EssayStudentListResponseItem) => {
    let link: string;
    if (essay.Status === EssayStatus.New) {
      const htmlBuilder: string[] = [];
      htmlBuilder.push(`<p>${this.t('essay.you-are-about-to-start-writing-essay')}</p>`);
      if (essay.TimeLimitMinutes) {
        const timeLimitHuman = msToHumanTime(essay.TimeLimitMinutes * 60 * 1000);
        htmlBuilder.push(`<p>${this.t('essay.this-essay-has-time-limit')}: <strong>${timeLimitHuman}</strong></p>`);
      }
      htmlBuilder.push(`<p>${this.t('essay.start-now')}</p>`);
      const confirmResult = await Swal.fire({
        icon: 'info',
        html: htmlBuilder.join(""),
        confirmButtonText: this.t('button.start'),
        cancelButtonText: this.t('button.no'),
        showCancelButton: true
      });
      if (!confirmResult.isConfirmed) {
        return;
      }
    }
    link = routes.EssayStudentLink(essay.EssayId);
    routerStore.navigateTo(link);
  }
}

export default withTranslation()(EssayStudentListScene);